import React, { useRef, useEffect } from "react"
import TextElement from "./Elements/TextElement.component"
import { ChartElement } from "./Elements/ChartElement"
import TableElement from "./Elements/TableElement"
import ImageElement from "./Elements/ImageElement.component"
import { TimeSeriesChartElement } from "./Elements/TimeSeriesChartElement"
import { PieChartElement } from "./Elements/PieChartElement"
import { useCanvasDimensions } from "features/Reporting/hooks/Dashboard/ReportingCanvas/useCanvasDimensions"
import { useElementConstraints } from "features/Reporting/hooks/Dashboard/ReportingCanvas/useElementConstraints"
import { useElementOperations } from "features/Reporting/hooks/Dashboard/ReportingCanvas/useElementOperations"
import { useDragAndDrop } from "features/Reporting/hooks/Dashboard/ReportingCanvas/useDragAndDrop"

import {
  CANVAS_WIDTH_UNITS,
  CANVAS_HEIGHT_UNITS,
  GRID_SIZE_UNITS,
} from "constants/powerpointCanvas"

// Types
import {
  SlideElement,
  DataElementConfig,
  ELEMENT_TYPE,
} from "types/ReportingDashboard/elements.types"
import { CodebookData } from "types/codebook.types"
import { QueryInstance } from "types/engine.types"
import { EngineQueryResult } from "types/engineOutput.types"
import { ElementRef } from "./ReportingDisplay.component"

/**
 * Props for the ReportingCanvas component
 */
export interface ReportingCanvasProps {
  /** Global filter string passed to data elements */
  filter?: string
  /** Array of element objects to render */
  elements?: SlideElement[]
  /** Callback when elements array changes */
  onElementsChange: (elements: SlideElement[]) => void
  /** Data structure for variable selection */
  codebook: CodebookData
  /** Instance ID for engine queries */
  queryInstance: QueryInstance | null
  /** Configurations for table/chart elements */
  tableConfigs?: Record<string, DataElementConfig>
  /** Callback for config updates */
  onTableConfigUpdate: (
    elementId: string,
    config: DataElementConfig | null
  ) => void
  /** Whether in template editing mode */
  isTemplateMode?: boolean
  /** Name of template being edited */
  templateName?: string
  /** Callback when element is deleted */
  onElementDelete: (elementId: string) => void
  /** Whether screen size is small */
  isSmallScreen?: boolean
  /** Callback for table data updates */
  onTableDataUpdate: (
    elementId: string,
    tableData: EngineQueryResult | null
  ) => void
  /** Sequential execution request handler */
  onElementExecutionRequest: (
    elementId: string,
    executeFunction: () => Promise<void>,
    reason?: string
  ) => void /** Element registration handler */
  onRegisterElementRef: (elementId: string, ref: ElementRef | null) => void
  /** Show grid lines */
  showGrid?: boolean
  /** Background color for the canvas */
  backgroundColor?: string
}

/**
 * ReportingCanvas - Interactive canvas component for arranging reporting elements.
 *
 * The main interactive area for creating and editing reports/dashboards. Supports:
 * - Drag-and-drop for adding new elements and repositioning existing ones
 * - Grid-based snapping and boundary constraints
 * - Template mode for creating reusable layouts
 * - Multiple element types (text, charts, tables, images)
 * - Sequential execution system for all data elements
 * - Responsive scaling and small screen support
 * - Real-time element property updates
 * - Context menu for toggling grid lines
 * - Customizable background colors
 */
const ReportingCanvas: React.FC<ReportingCanvasProps> = ({
  filter,
  elements = [],
  onElementsChange,
  codebook,
  queryInstance,
  tableConfigs = {},
  onTableConfigUpdate,
  isTemplateMode = false,
  templateName = "",
  onElementDelete,
  isSmallScreen = false,
  onTableDataUpdate,
  onElementExecutionRequest,
  onRegisterElementRef,
  showGrid = true,
  backgroundColor = "#ffffff",
}) => {
  const canvasRef = useRef<HTMLDivElement>(null)

  // Custom hooks
  const { canvasDimensions, scaleX, scaleY } = useCanvasDimensions(
    canvasRef,
    CANVAS_WIDTH_UNITS,
    CANVAS_HEIGHT_UNITS
  )

  const { constrainToBoundaries, snapToGrid, DEFAULT_ELEMENT_SIZES } =
    useElementConstraints(
      elements,
      tableConfigs,
      GRID_SIZE_UNITS,
      CANVAS_WIDTH_UNITS,
      CANVAS_HEIGHT_UNITS
    )

  const { handleElementUpdate, isTemplateElement } = useElementOperations(
    elements,
    onElementsChange,
    isTemplateMode,
    constrainToBoundaries
  )

  const { draggedElement, handleDragOver, handleElementDragStart, handleDrop } =
    useDragAndDrop(
      elements,
      onElementsChange,
      isTemplateMode,
      isTemplateElement,
      snapToGrid,
      constrainToBoundaries,
      canvasDimensions,
      canvasRef,
      CANVAS_WIDTH_UNITS,
      CANVAS_HEIGHT_UNITS,
      DEFAULT_ELEMENT_SIZES
    )
  // Custom context menu handler that only triggers on canvas background
  const handleCanvasContextMenu = (e: React.MouseEvent<HTMLDivElement>) => {
    // Prevent default context menu since grid toggle is now in header
    e.preventDefault()
  }

  // Effect to re-apply boundary constraints
  useEffect(() => {
    if (canvasDimensions.width > 0 && elements.length > 0) {
      let needsUpdate = false
      const updatedElements = elements.map((element: SlideElement) => {
        const { x: constrainedX, y: constrainedY } = constrainToBoundaries(
          element.id,
          element.x,
          element.y
        )
        if (constrainedX !== element.x || constrainedY !== element.y) {
          needsUpdate = true
          return { ...element, x: constrainedX, y: constrainedY }
        }
        return element
      })

      if (needsUpdate) {
        onElementsChange(updatedElements)
      }
    }
  }, [canvasDimensions, elements, onElementsChange, constrainToBoundaries])

  const headerOffset_px: number = isTemplateMode ? 28 : 0

  /**
   * Handle save template changes event
   */
  const handleSaveTemplateChanges = (): void => {
    window.dispatchEvent(new CustomEvent("saveTemplateChanges"))
  }

  return (
    <>
      <div className="flex justify-center items-center w-full h-full">
        <div
          ref={canvasRef}
          onDragOver={handleDragOver}
          onDrop={handleDrop}
          onContextMenu={handleCanvasContextMenu}
          className={`border-2 border-dashed ${
            isTemplateMode ? "border-blue-400" : "border-gray-300"
          } rounded-lg relative overflow-hidden shadow-lg canvas-background`}
          style={{
            aspectRatio: `${CANVAS_WIDTH_UNITS} / ${CANVAS_HEIGHT_UNITS}`,
            width: isSmallScreen ? "92%" : "98%",
            height: "auto",
            transform: isSmallScreen ? "scale(0.9)" : "none",
            transformOrigin: "center center",
            margin: "auto",
            backgroundColor: backgroundColor, // Apply background color
          }}
          aria-label="Reporting Canvas"
        >
          {/* Template Mode Banner */}
          {isTemplateMode && (
            <div
              className="absolute top-0 left-0 right-0 bg-blue-500 text-white py-1 px-4 text-sm font-medium z-10 flex justify-between items-center"
              style={{ height: `${headerOffset_px}px` }}
            >
              <span>Editing Template: {templateName || "Untitled"}</span>
              <div className="flex items-center">
                <button
                  onClick={handleSaveTemplateChanges}
                  className="mr-2 bg-white text-blue-600 hover:bg-blue-100 px-2 py-0.5 rounded text-xs font-medium transition-colors focus:outline-none focus:ring-1 focus:ring-white"
                  title="Save template changes"
                  type="button"
                >
                  Save Changes
                </button>
                <span className="text-xs bg-blue-600 px-2 py-0.5 rounded">
                  Template Mode
                </span>
              </div>
            </div>
          )}
          {/* Grid Background */}
          {showGrid && (
            <div
              className="absolute inset-0 pointer-events-none canvas-background"
              style={{
                backgroundImage:
                  "linear-gradient(#ddd 1px, transparent 1px), linear-gradient(90deg, #ddd 1px, transparent 1px)",
                backgroundSize: `${GRID_SIZE_UNITS * scaleX}px ${
                  GRID_SIZE_UNITS * scaleY
                }px`,
                opacity: 0.2,
                backgroundPosition: `0 ${headerOffset_px}px`,
                top: `${headerOffset_px}px`,
                bottom: 0,
                left: 0,
                right: 0,
              }}
              aria-hidden="true"
            />
          )}
          {/* Render Canvas Elements - ALL DATA ELEMENTS GET SEQUENTIAL EXECUTION PROPS */}
          {elements.map((element: SlideElement) => {
            const isFromTemplate = isTemplateElement(element)
            const isDraggable = isTemplateMode || !isFromTemplate
            const canDelete = isTemplateMode || !isFromTemplate

            const pixelX = element.x * scaleX
            const pixelY = element.y * scaleY + headerOffset_px
            const pixelWidth = element.width
              ? element.width * scaleX
              : undefined
            const pixelHeight = element.height
              ? element.height * scaleY
              : undefined

            return (
              <div
                key={element.id}
                draggable={isDraggable}
                onDragStart={(e: React.DragEvent<HTMLDivElement>) =>
                  handleElementDragStart(element, e)
                }
                className={`absolute ${
                  isDraggable ? "cursor-move" : "cursor-not-allowed"
                } ${!isTemplateMode && isFromTemplate ? "opacity-70" : ""}`}
                style={{
                  left: `${pixelX}px`,
                  top: `${pixelY}px`,
                  width: pixelWidth ? `${pixelWidth}px` : "auto",
                  height: pixelHeight ? `${pixelHeight}px` : "auto",
                  border: "none",
                  zIndex: draggedElement?.id === element.id ? 10 : 1,
                }}
                role="group"
                aria-label={element.type}
              >
                {!isTemplateMode && isFromTemplate && (
                  <div className="absolute top-0 right-0 bg-blue-500 text-white text-xs px-1 z-10">
                    Template
                  </div>
                )}

                {element.type === ELEMENT_TYPE.CHART && (
                  <ChartElement
                    filter={filter}
                    element={element}
                    codebook={codebook}
                    queryInstance={queryInstance}
                    onUpdate={handleElementUpdate}
                    config={tableConfigs[element.id]}
                    onConfigUpdate={(config: DataElementConfig | null) =>
                      onTableConfigUpdate(element.id, config)
                    }
                    onDeleteRequest={
                      canDelete ? () => onElementDelete(element.id) : undefined
                    }
                    isDashboard={true}
                    onElementExecutionRequest={onElementExecutionRequest}
                    onRegisterElementRef={onRegisterElementRef}
                  />
                )}
                {element.type === ELEMENT_TYPE.PIE_CHART && (
                  <PieChartElement
                    filter={filter}
                    element={element}
                    codebook={codebook}
                    queryInstance={queryInstance}
                    onUpdate={handleElementUpdate}
                    config={tableConfigs[element.id]}
                    onConfigUpdate={(config: DataElementConfig | null) =>
                      onTableConfigUpdate(element.id, config)
                    }
                    onDeleteRequest={
                      canDelete ? () => onElementDelete(element.id) : undefined
                    }
                    isDashboard={true}
                    onElementExecutionRequest={onElementExecutionRequest}
                    onRegisterElementRef={onRegisterElementRef}
                  />
                )}
                {element.type === ELEMENT_TYPE.TIME_SERIES_CHART && (
                  <TimeSeriesChartElement
                    element={element}
                    codebook={codebook}
                    queryInstance={queryInstance}
                    onUpdate={handleElementUpdate}
                    config={tableConfigs[element.id]}
                    onConfigUpdate={(config: DataElementConfig | null) =>
                      onTableConfigUpdate(element.id, config)
                    }
                    onDeleteRequest={
                      canDelete ? () => onElementDelete(element.id) : undefined
                    }
                    isDashboard={true}
                    onElementExecutionRequest={onElementExecutionRequest}
                    onRegisterElementRef={onRegisterElementRef}
                  />
                )}
                {(element.type === ELEMENT_TYPE.TEXT ||
                  element.type === ELEMENT_TYPE.HEADING) && (
                  <TextElement
                    element={element}
                    onUpdate={handleElementUpdate}
                    readonly={!isTemplateMode && isFromTemplate}
                    onDeleteRequest={
                      canDelete ? () => onElementDelete(element.id) : undefined
                    }
                  />
                )}
                {element.type === ELEMENT_TYPE.TABLE && (
                  <TableElement
                    filter={filter}
                    element={element}
                    codebook={codebook}
                    queryInstance={queryInstance}
                    onUpdate={handleElementUpdate}
                    config={tableConfigs[element.id]}
                    onConfigUpdate={(config: DataElementConfig | null) =>
                      onTableConfigUpdate(element.id, config)
                    }
                    onDeleteRequest={
                      canDelete ? () => onElementDelete(element.id) : undefined
                    }
                    onTableDataUpdate={onTableDataUpdate}
                    onElementExecutionRequest={onElementExecutionRequest}
                    onRegisterElementRef={onRegisterElementRef}
                  />
                )}
                {element.type === ELEMENT_TYPE.IMAGE && (
                  <ImageElement
                    element={element}
                    onUpdate={handleElementUpdate}
                    readonly={!isTemplateMode && isFromTemplate}
                    onDeleteRequest={
                      canDelete ? () => onElementDelete(element.id) : undefined
                    }
                  />
                )}
              </div>
            )
          })}
          {elements.length === 0 && (
            <div
              className={`flex items-center justify-center h-full text-gray-500 canvas-background ${
                isTemplateMode ? `pt-[${headerOffset_px}px]` : ""
              }`}
              aria-live="polite"
            >
              {isTemplateMode
                ? "Drag elements here to create your template"
                : "Drag elements here to create your page"}
            </div>
          )}{" "}
        </div>
      </div>
    </>
  )
}

export default ReportingCanvas
